import { Locale } from '@lalalilo/enums'
import * as React from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import { Logo, LogoTheme } from '../common/logo'
import { Color } from '../common/style/color'
import {
  MOBILE_BURGER_MENU_LEFT_OFFSET,
  MOBILE_BURGER_MENU_TOP_OFFSET,
} from '../common/style/components/burger-menu'
import {
  InternalLink,
  NewTabLink,
  RoundLink,
} from '../common/style/components/links'
import { mobile, tablet } from '../common/style/media-queries'
import { P2IA_APP_URL, TeachAppPath, useTeachAppLink } from '../external-links'
import { getLocale } from '../i18n/locale'
import { BurgerMenuProps } from './burger-menu'

export const headerHeightPx = { desktop: 60, mobile: 50 }

export const Header = () => {
  const [t] = useTranslation('menu')
  const teachAppLink = useTeachAppLink()

  return (
    <>
      <MobileBurgerMenu
        left={MOBILE_BURGER_MENU_LEFT_OFFSET}
        top={MOBILE_BURGER_MENU_TOP_OFFSET}
        size={headerHeightPx.mobile - 8}
      />
      <Wrapper>
        <ContentWrapper>
          <LogoWrapper>
            <StyledLogo logoTheme={LogoTheme.DARK} />
          </LogoWrapper>

          <Nav>
            <LinksWrapper>
              <Link to="/pricing" eventLabel="pricing in header">
                {t('pricing')}
              </Link>

              <Link to={t('blog_url')} eventLabel="blog in header">
                {t('blog')}
              </Link>

              <Link
                to={getLocale() === Locale.EN ? '/resources' : P2IA_APP_URL}
                eventLabel="resources in header"
              >
                {t('resources')}
              </Link>
            </LinksWrapper>
            <LoginLink
              to={teachAppLink(TeachAppPath.PORTAL)}
              eventLabel="menu login"
            >
              {t('login')}
            </LoginLink>

            <SignupLink
              to={teachAppLink(TeachAppPath.GLOBAL_SIGN_UP)}
              eventLabel="menu signup"
            >
              {t('signup')}
            </SignupLink>
          </Nav>
        </ContentWrapper>
      </Wrapper>
      <Placeholder />
    </>
  )
}

const Wrapper = styled.header`
  position: fixed;
  z-index: 20;

  width: 100%;
  height: ${headerHeightPx.desktop}px;

  overflow: visible;

  background-color: #ffffff;
  box-shadow: 0 1px 1px 0 rgba(51, 69, 91, 0.1);

  @media ${mobile} {
    justify-content: center;
    height: ${headerHeightPx.mobile}px;
    z-index: 10;
  }
`

const ContentWrapper = styled.div`
  width: 100%;
  height: 100%;
  max-width: 1000px;
  margin: 0 auto;
  box-sizing: border-box;
  padding: 0 20px;

  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  @media ${mobile} {
    justify-content: center;
  }
`

const Placeholder = styled.div`
  position: static;
  width: 100%;
  height: ${headerHeightPx.desktop}px;
  @media ${mobile} {
    height: ${headerHeightPx.mobile}px;
  }
`

const LogoWrapper: React.FC = ({ children, ...props }) => (
  <InternalLink to={'/' + window.location.search} {...props}>
    {children}
  </InternalLink>
)

const StyledLogo = styled(Logo)`
  object-fit: contain;
`

const Nav = styled.nav`
  display: flex;
  flex-direction: row;
  align-items: center;

  @media ${mobile} {
    display: none;
  }
`

const LinksWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 55px;
  margin-right: 30px;

  @media ${tablet} {
    gap: 20px;
    margin: 0 20px;
  }
`

const Link = styled(NewTabLink)`
  height: 100%;
  display: flex;
  align-items: center;
  cursor: pointer;
  &:hover {
    background-color: rgba(130, 178, 255, 0.1);
  }
  opacity: 0.9;
  font-size: 14px;
  font-weight: bold;
  color: #33455b;
`

const LoginLink = styled(RoundLink)`
  background-color: ${Color.white};
  border: 1px solid ${Color.purple_600};
  color: ${Color.purple_600};

  &:hover {
    background-color: ${Color.purple_600};
    color: ${Color.white};
  }
`

const SignupLink = styled(RoundLink)`
  background-color: ${Color.purple_600};
  opacity: 0.8;

  margin-left: 20px;

  @media ${tablet} {
    margin-left: 15px;
  }

  &:hover {
    opacity: 1;
  }
`

const BurgerMenuBase = React.lazy(() => import('./burger-menu'))
const BurgerMenu: React.FC<BurgerMenuProps> = props => (
  <React.Suspense fallback={null}>
    <BurgerMenuBase {...props} />
  </React.Suspense>
)

const MobileBurgerMenu = styled(BurgerMenu)`
  z-index: 21;
  display: none;
  @media ${mobile} {
    display: block;
  }
`
